import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Route,
  CloudCog,
  Infinity as InfinityIcon,
  MonitorSmartphone,
  MessagesSquare,
  CloudUpload,
  Rocket,
  Gauge,
  Handshake,
  ArrowRight,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const cloudNative = [
  {
    icon: Route,
    title: "Strategy and Roadmap",
    desc: "We assess your existing IT infrastructure and develop comprehensive strategic roadmaps tailored to your business needs. Our goal is to ensure your IT investments align with your long-term objectives.",
  },
  {
    icon: CloudCog,
    title: "Cloud Native Enablement",
    desc: "Our experts assist with PaaS migration (Re-Factor, Re-Host, Re-Architect) and custom application development, ensuring a smooth transition to cloud-native environments.",
  },
  {
    icon: InfinityIcon,
    title: "DevOps Transformation",
    desc: "We help you assess and set up DevOps platforms, optimizing CI/CD pipelines to enhance your software development and deployment processes.",
  },
  {
    icon: MonitorSmartphone,
    title: "Cloud Application Management",
    desc: "We offer 24/7 monitoring, administration, and management of your cloud servers. Our services include database design, implementation, storage, backup management, and security management.",
  },
];

const cloudAdoption = [
  {
    icon: MessagesSquare,
    title: "Consulting Services",
    desc: "We provide specialized consulting services to assess your cloud infrastructure and craft transformation roadmaps using agile and collaborative methods.",
  },
  {
    icon: CloudUpload,
    title: "Cloud Build and Migration",
    desc: "We design, build, and migrate public, private, and hybrid cloud environments. Our services include pilot migrations with thorough pre-migration testing.",
  },
  {
    icon: Rocket,
    title: "Cloud Enablement",
    desc: "Our team excels in cloud-native development and re-engineering existing systems to optimize them for cloud environments.",
  },
  {
    icon: Gauge,
    title: "Cloud Operation and Management",
    desc: "We monitor, administer, and manage your cloud servers continuously. Our services ensure optimal performance and security, including database design, implementation, and backup management.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const AppsInfrastructure = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.appsInfrastructure} jsonLd={[buildBreadcrumbJsonLd(seoConfig.appsInfrastructure.crumbs)]} />
      <PageHero
        title="Apps and Infrastructure"
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Technology Services", href: "/services/technology-services" },
          { label: "Apps and Infrastructure" },
        ]}
        lightfall
      />

      <section data-testid="apps-intro" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/Application.jpg"
                alt="Applications and Infrastructure"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Applications and Infrastructure Services
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
              Building and Managing Future-Ready Applications
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              We specialize in creating modern applications using the latest development methodologies and the most
              suitable infrastructure for your business needs. Our services ensure your applications are scalable,
              secure, and optimized for performance.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="cloud-native" className="pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Future-Proof and Change-Tolerant Solutions
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              Cloud Native Services
            </h2>
            <div className="mt-5 h-0.5 w-12 bg-miti-blue" />
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Our Cloud Native services encompass the selection of the right methodologies, platforms, and
              architectural patterns to create resilient and scalable applications. We focus on enabling your
              business to adapt and thrive in a rapidly changing technological landscape.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            {cloudNative.map((c, i) => (
              <motion.div
                key={c.title}
                {...fadeUp}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`cloud-native-card-${i + 1}`}
                className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-8 hover:border-miti-blue/40 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center shrink-0">
                  <c.icon size={22} strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-slate-900">{c.title}</h3>
                  <p className="mt-2.5 text-sm md:text-base text-slate-500 leading-relaxed">{c.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="cloud-adoption" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              End-to-End Cloud Adoption
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              IaaS, SaaS, PaaS
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-500">
              Our IaaS, SaaS, and PaaS services support your entire cloud journey, from readiness assessment and
              secure architecture design to environment setup and automated tool-led migration.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {cloudAdoption.map((c, i) => (
              <motion.div
                key={c.title}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`cloud-adoption-card-${i + 1}`}
                className="text-center rounded-2xl border border-slate-200 bg-white p-8 hover:border-miti-blue/40 transition-colors duration-300"
              >
                <div className="mx-auto w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center">
                  <c.icon size={22} strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-heading font-semibold text-lg text-slate-900 leading-snug">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="apps-cta" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Start a Conversation
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Ready to Modernize Your Applications?
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Connect with our experts today to explore how our Applications and Infrastructure services can drive
              your business forward. Let's build the future together.
            </p>
            <a
              href="/contact"
              data-testid="apps-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/cta-bg.jpg"
                alt="Start a Conversation"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AppsInfrastructure;
